import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	Sliders,
	File,
	ArrowRightCircle,
	ArrowLeftCircle,
	DownloadCloud,
	Download,
	UploadCloud,
	FilePlus,
	Save,
	ShoppingBag,
} from 'react-feather';
import {
	fetchMaterials,
	fetchMaterialById,
	importMaterials,
	exportMaterials,
	fetchCategories,
} from '../slices/materialsSlice';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import PricingDetails from '../components/Pricing/PricingDetails';
import NewPricing from '../components/Pricing/NewPricing';

export default function Pricing() {
	const dispatch = useDispatch();
	const material = useSelector((state) => state?.materials?.items) || [];
	const [newMaterialForm, setNewMaterialForm] = useState(false);
	const token = useSelector((state) => state.auth.token);
	// const [inventory, setInventory] = useState(false);
	const [materialId, setMaterialId] = useState(null);

	const openDetails = (materialId) => {
		setMaterialId(materialId);
		dispatch(fetchMaterials());
		dispatch(fetchMaterialById(materialId));
	};

	const closeDetails = () => {
		setNewMaterialForm(null);
		if (token) dispatch(fetchMaterials(), fetchMaterialById());
	};

	// const [isLoading, setIsLoading] = useState(true);

	// useEffect(() => {
	// 	setTimeout(() => {
	// 		setIsLoading(false);
	// 	}, 1500);
	// }, []);

	// if (isLoading) {
	// 	return <p>Loading...</p>;
	// }

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);
	// const [categoryList, setCategoryList] = useState([]);
	const categoryList =
		useSelector((state) => state?.materials?.categories) || [];

	useEffect(() => {
		// We only fetch when the currentPage changes, not on the initial render
		if (currentPage > 1) {
			dispatch(fetchMaterials({ pageSize, page: currentPage }));
		}
	}, [dispatch, currentPage, pageSize]);

	useEffect(() => {
		// This single hook now handles all initial data fetching.
		// It just "fires" the actions. Redux and useSelector handle the rest.
		dispatch(fetchMaterials({ pageSize, page: 1 }));
		dispatch(fetchCategories());
	}, [dispatch, pageSize]);

	// Pagination Handlers
	const handleNextPage = () => {
		setCurrentPage(currentPage + 1);
	};

	const handlePreviousPage = () => {
		if (currentPage > 1) setCurrentPage(currentPage - 1);
	};

	const handlePageSizeChange = (event) => {
		setPageSize(parseInt(event.target.value, 10));
		setCurrentPage(1); // Reset to first page with new page size
	};

	// Import and export data in bulk

	const fileInputRef = useRef(null);
	const [importError, setImportError] = useState('');
	const [importSuccess, setImportSuccess] = useState('');
	const [downloadCategories, setDownloadCategories] = useState(0);

	// Working import file option, changed to add the success and error messages
	// const handleImport = (event) => {
	// 	const files = event.target.files;
	// 	if (files.length > 0) {
	// 		const file = files[0];
	// 		dispatch(importMaterials(file));
	// 	}
	// };

	const handleImport = async (e) => {
		setImportError('');
		setImportSuccess('');

		const files = e.target.files;
		const file = files && files[0];

		if (!file) {
			setImportError('No file selected.');
			return;
		}

		const isValid =
			file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';

		if (!isValid) {
			setImportError('Invalid file type. Please upload a CSV file.');
			return;
		}

		try {
			// assuming importMaterials expects the File and builds FormData internally
			const action = await dispatch(importMaterials(file));

			if (action.meta && action.meta.requestStatus === 'fulfilled') {
				setImportSuccess('File imported successfully!');
				dispatch(fetchMaterials({ pageSize, page: 1 }));
			} else {
				console.error('Import failed:', action);
				setImportError(
					(action && action.payload) ||
						(action && action.error && action.error.message) ||
						'Import failed. Please try again.'
				);
			}
		} catch (err) {
			console.error('Unexpected import error:', err);
			setImportError('Unexpected error while importing file.');
		} finally {
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		}
	};

	// Filter components
	const [filterName, setFilterName] = useState('');
	const [filterDescription, setFilterDescription] = useState('');
	const [filterCategoryId, setFilterCategoryId] = useState('');
	const [filterWidth, setFilterWidth] = useState('');
	const [filterLength, setFilterLength] = useState('');
	const [filterHeight, setFilterHeight] = useState('');

	const applyFilters = () => {
		// When applying a new filter, always reset to page 1
		setCurrentPage(1);
		dispatch(
			fetchMaterials({
				categoryId: filterCategoryId,
				pageSize: pageSize, // Use the page size from state, not 10000
				page: 1, // Fetch the first page of the filtered results
				sortOrder: 'ASC',
				name: filterName,
				width: filterWidth,
				length: filterLength,
				height: filterHeight,
				description: filterDescription,
			})
		);
	};

	const resetFilters = () => {
		setFilterName('');
		setFilterDescription('');
		setFilterCategoryId('');
		setFilterWidth('');
		setFilterLength('');

		// Also reset to page 1 and fetch the default list
		setCurrentPage(1);
		dispatch(fetchMaterials({ pageSize: pageSize, page: 1 }));
	};

	// Toggle sections
	const [filterSection, setFilterSections] = useState({
		filterOptions: false,
		exportMaterials: false,
		importMaterials: false,
	});

	const toggleSection = (sectionName) => {
		setFilterSections((prevSections) => {
			const isCurrentlyOpen = prevSections[sectionName];
			// Close everything first
			const newState = {
				filterOptions: false,
				exportMaterials: false,
				importMaterials: false,
			};
			// If it wasn't open, open it now
			if (!isCurrentlyOpen) {
				newState[sectionName] = true;
			}
			return newState;
		});
	};

	const handleExport = () => {
		// Example CategoryId, adjust based on your UI/state
		const categoryId = { CategoryId: downloadCategories };

		dispatch(exportMaterials(categoryId)).then((action) => {
			if (action.meta.requestStatus === 'fulfilled') {
				// API returns the data in the response, convert to csv and download
				const csv = action.payload; // Ensure this is a string in CSV format
				const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
				const url = window.URL.createObjectURL(blob);

				// Correctly create the <a> element
				const a = document.createElement('a');
				a.href = url;
				a.download = 'prices.csv'; // Name the download file here
				document.body.appendChild(a); // Append the <a> element to the body
				a.click(); // Simulate click on the <a> element to start the download

				// Cleanup
				window.URL.revokeObjectURL(url);
				document.body.removeChild(a); // Remove the <a> element after download
			} else {
				// Handle rejection or errors here
				console.error(
					'Export failed:',
					action.error?.message || 'Unknown error'
				);
			}
		});
	};

	return (
		<div className="flex flex-col h-screen">
			<Header />
			<div className="flex flex-1 overflow-hidden">
				<Sidebar />
				<main className="flex-1 overflow-y-auto bg-slate-50 modern-dashboard">
					<div className="content-header">
						<h1>
							<ShoppingBag size={32} /> Pricing Management
						</h1>
					</div>

					<div className="table-card">
						<div className="action-row">
							<div className="search-wrapper">
								<Sliders size={18} />
								<input 
									type="text" 
									placeholder="Quick search products..." 
									value={filterName}
									onChange={(e) => setFilterName(e.target.value)}
								/>
							</div>
							<div className="flex gap-2">
								<button 
									className="quick-access-btn"
									onClick={() => toggleSection('filterOptions')}
								>
									<span>All Filters</span>
								</button>
								<button 
									className="quick-access-btn"
									onClick={() => toggleSection('importMaterials')}
								>
									<UploadCloud size={20} />
									<span>Import</span>
								</button>
								<button 
									className="quick-access-btn"
									onClick={() => toggleSection('exportMaterials')}
								>
									<DownloadCloud size={20} />
									<span>Export</span>
								</button>
								<button 
									className="quick-access-btn primary"
									onClick={() => setNewMaterialForm(true)}
								>
									<FilePlus size={20} />
									<span>Add Item</span>
								</button>
							</div>
						</div>

						{/* Import/Export Tabs */}
						{(filterSection.importMaterials || filterSection.exportMaterials) && (
							<div className="filters-grid mb-6">
								{filterSection.importMaterials && (
									<div className="flex flex-col gap-2">
										<button
											className="quick-access-btn"
											onClick={() => fileInputRef.current.click()}
										>
											<Save size={18} /> Select CSV to Import
										</button>
										<input ref={fileInputRef} type="file" hidden onChange={handleImport} />
										{importSuccess && <div className="text-sm text-green-600 font-medium px-2">{importSuccess}</div>}
										{importError && <div className="text-sm text-red-600 font-medium px-2">{importError}</div>}
									</div>
								)}

								{filterSection.exportMaterials && (
									<div className="flex gap-2">
										<select
											className="flex-1"
											onChange={(e) => setDownloadCategories(e.target.value)}
											value={downloadCategories}
										>
											<option value="0">All Categories</option>
											{categoryList.slice().sort((a, b) => a.name.localeCompare(b.name)).map((category) => (
												<option key={category.id} value={category.id}>{category.name} ({category.id})</option>
											))}
										</select>
										<button className="quick-access-btn primary" onClick={handleExport}>
											<Download size={18} /> Export
										</button>
									</div>
								)}
							</div>
						)}

						{/* Filters Toggle Area */}
						{filterSection.filterOptions && (
							<div className="filters-grid">
								<input
									type="text"
									placeholder="Name"
									value={filterName}
									onChange={(e) => setFilterName(e.target.value)}
								/>
								<input
									type="text"
									placeholder="Description"
									value={filterDescription}
									onChange={(e) => setFilterDescription(e.target.value)}
								/>
								<input
									type="number"
									placeholder="Width"
									value={filterWidth}
									onChange={(e) => setFilterWidth(e.target.value)}
								/>
								<input
									type="number"
									placeholder="Length"
									value={filterLength}
									onChange={(e) => setFilterLength(e.target.value)}
								/>
								<input
									type="number"
									placeholder="Height"
									value={filterHeight}
									onChange={(e) => setFilterHeight(e.target.value)}
								/>
								<select
									value={filterCategoryId}
									onChange={(e) => setFilterCategoryId(e.target.value)}
								>
									<option value="">All Categories</option>
									{categoryList.slice().sort((a, b) => a.name.localeCompare(b.name)).map((category) => (
										<option key={category.id} value={category.id}>{category.name}</option>
									))}
								</select>
								<div className="filter-btn-group">
									<button onClick={applyFilters} className="search">Apply</button>
									<button onClick={resetFilters} className="reset">Reset</button>
								</div>
							</div>
						)}

						<table className="modern-table">
							<thead>
								<tr>
									<th>Product</th>
									<th>Category</th>
									<th>Specs / Size</th>
									<th className="text-right">Actions</th>
								</tr>
							</thead>
							<tbody>
								{material
									?.slice()
									.reverse()
									.filter((mate) => mate.active)
									.map((mate, index) => (
										<tr key={mate?.id} onClick={() => openDetails(mate?.id)}>
											<td>
												<div className="flex items-center gap-3">
													<div className={`avatar-initials bg-avatar-${(index % 5) + 1}`}>
														{mate?.name?.substring(0, 2).toUpperCase()}
													</div>
													<div className="font-semibold text-slate-800">
														{mate?.name}
													</div>
												</div>
											</td>
											<td>
												<span className="text-slate-500 font-medium">{mate?.categoryName} ({mate?.categoryId})</span>
											</td>
											<td>
												<div className="flex items-center gap-1 text-slate-600 font-mono text-sm">
													{mate?.width ? <span>{mate?.width}W</span> : ''}
													{mate?.length ? <span>× {mate?.length}L</span> : ''}
													{mate?.height ? <span>× {mate?.height}H</span> : ''}
													<span className="ml-1 text-slate-400">({mate?.measurementUnit})</span>
												</div>
											</td>
											<td className="text-right">
												<button
													onClick={(e) => {
														e.stopPropagation();
														openDetails(mate?.id);
													}}
													className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
												>
													<File size={20} />
												</button>
											</td>
										</tr>
									))}
							</tbody>
						</table>

						<div className="pagination-modern">
							<div className="pagination-info">
								Page <strong>{currentPage}</strong>
							</div>
							<div className="flex items-center gap-4">
								<select 
									className="px-2 py-1 rounded-md border border-slate-200 text-sm outline-none"
									value={pageSize} 
									onChange={handlePageSizeChange}
								>
									<option value="10">10 / page</option>
									<option value="50">50 / page</option>
									<option value="100">100 / page</option>
									<option value="500">500 / page</option>
								</select>
								<div className="pagination-btns">
									<button onClick={handlePreviousPage} disabled={currentPage === 1}>
										<ArrowLeftCircle size={18} /> Prev
									</button>
									<button onClick={handleNextPage}>
										Next <ArrowRightCircle size={18} />
									</button>
								</div>
							</div>
						</div>
					</div>

					{materialId && (
						<PricingDetails
							onClose={() => setMaterialId(null)}
							materialId={materialId}
						/>
					)}
					{newMaterialForm && <NewPricing onClose={() => closeDetails()} />}
				</main>
			</div>
		</div>
	);
}
